import { AgreeToTermsPolicy, experience, ExtraParamsKey, SignInMode } from '@logto/schemas';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import SingleSignOnFormModeContext from '@/Providers/SingleSignOnFormModeContextProvider/SingleSignOnFormModeContext';
import landingAiLogo from '@/assets/icons/landing-ai-logo.svg?url';
import GoogleOneTap from '@/components/GoogleOneTap';
import IdentifierRegisterForm from '@/components/IdentifierRegisterForm';
import TextLink from '@/components/TextLink';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';

import ErrorPage from '../ErrorPage';
import LandingSocialButtons from '../SignIn/LandingSocialButtons';

import styles from './LandingRegister.module.scss';

const LandingRegisterFooter = () => {
  const { t } = useTranslation();
  const { signUpMethods, socialConnectors, signInMode, signInMethods, singleSignOnEnabled } =
    useSieMethods();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigateWithPreservedSearchParams();
  const [params] = useSearchParams();

  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const handleSsoNavigation = useCallback(async () => {
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }
    navigate('/single-sign-on/email');
  }, [agreeToTermsPolicy, navigate, termsValidation]);

  if (params.get(ExtraParamsKey.OneTimeToken)) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  if (showSingleSignOnForm) {
    return null;
  }

  return (
    <>
      {singleSignOnEnabled && (
        <>
          <div className={styles.singleSignOn}>
            {t('description.use')}{' '}
            <TextLink text="action.single_sign_on" onClick={handleSsoNavigation} />
          </div>
          {signInMethods.length === 0 &&
            socialConnectors.length === 0 &&
            agreeToTermsPolicy === AgreeToTermsPolicy.Manual && (
              <TermsAndPrivacyCheckbox className={styles.checkbox} />
            )}
        </>
      )}
      {signInMode === SignInMode.SignInAndRegister && signInMethods.length > 0 && (
        <div className={styles.createAccount}>
          {t('description.have_account')} <TextLink replace to="/sign-in" text="action.sign_in" />
        </div>
      )}
      {/* LandingAI-styled social buttons (always visible, no connector guard) */}
      {signUpMethods.length > 0 && (
        <div className={styles.socialSection}>
          <div className={styles.dividerLine} />
          <LandingSocialButtons isSingleSignOnEnabled={false} />
        </div>
      )}
    </>
  );
};

const LandingRegister = () => {
  const { signUpMethods, signInMode } = useSieMethods();
  const { agreeToTermsPolicy } = useTerms();

  if (!signInMode) {
    return <ErrorPage />;
  }

  if (signInMode === SignInMode.SignIn) {
    return <Navigate to="/sign-in" />;
  }

  return (
    <LandingPageLayout title="description.create_your_account" logoOverride={landingAiLogo}>
      <GoogleOneTap context="signup" />
      <SingleSignOnFormModeContextProvider>
        {signUpMethods.length > 0 && (
          <IdentifierRegisterForm signUpMethods={signUpMethods} className={styles.main} />
        )}
        {signUpMethods.length === 0 && (
          <>
            {agreeToTermsPolicy !== AgreeToTermsPolicy.Automatic && (
              <TermsAndPrivacyCheckbox className={styles.terms} />
            )}
            <LandingSocialButtons isSingleSignOnEnabled={false} />
          </>
        )}
        <LandingRegisterFooter />
        {agreeToTermsPolicy === AgreeToTermsPolicy.Automatic && (
          <TermsAndPrivacyLinks className={styles.terms} />
        )}
      </SingleSignOnFormModeContextProvider>
    </LandingPageLayout>
  );
};

export default LandingRegister;
